// Phase 2 mock data layer. The Lanes module's design depends on status,
// totes, auctions and auction-end-date values that the BE doesn't
// supply yet — until it does, we synthesise them on the FE from the
// lane's barcode. The function is pure and deterministic so refreshing
// the screen never reshuffles the rows.
//
// When the BE feed lands, swap this for the real fields in
// useLanes.ts — no other call sites need changing.

import { STATUS_SLUGS, type StatusSlug, statusFamily } from "./laneStatus";
import { statusFlags } from "./laneStatusConfig";

export interface MockLaneFields {
    status: StatusSlug;
    totes: number;
    auctions: number;
    auction_end_date: string | null;
}

// djb2-style string hash. 32-bit, no external dep, stable across reloads.
const hash = (s: string): number => {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    }
    return h;
};

const isoDateOffset = (days: number): string => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

export const todayISO = (): string => isoDateOffset(0);

// Board reset: when true, every lane reads as "Lane Empty" — no totes,
// auctions or end date — instead of the synthesised demo state. This is
// the empty starting point the warehouse wanted; flip to false to bring
// the varied demo data (below) back. (Lane status has no backend yet, so
// this mock is still the source — see useLanes.ts.)
const FORCE_ALL_LANES_EMPTY = true;

export const mockLane = (barcode: string): MockLaneFields => {
    if (FORCE_ALL_LANES_EMPTY) {
        return { status: "lane-empty", totes: 0, auctions: 0, auction_end_date: null };
    }

    const h = hash(barcode);
    const status = STATUS_SLUGS[h % STATUS_SLUGS.length];
    const family = statusFamily(status);
    // Which of totes / auctions / auction-end-date this status carries is
    // an admin-overridable attribute (see laneStatusConfig.ts). The mock
    // still derives realistic *magnitudes* from the family, but only
    // populates a field when its flag is on — so a status configured with
    // (e.g.) "no auctions" never shows auction counts in the demo.
    const flags = statusFlags(status);

    // Realistic warehouse counts: stock lanes tens of totes; auction
    // lanes typically hundreds-to-thousands of auctions (one auction
    // = one lot card). The mock spans 0–4 digits so the layout gets
    // tested against worst-case widths without us needing real data.
    let totes = 0;
    if (flags.totes) {
        if (family === "stock") totes = ((h >> 3) % 80) + 5;            //   5 – 84
        else if (family === "outbound") totes = ((h >> 3) % 150) + 20;  //  20 – 169
        else if (family === "auction") totes = ((h >> 3) % 400) + 50;   //  50 – 449
        else totes = ((h >> 3) % 30) + 1;                              //   1 – 30
    }

    let auctions = 0;
    if (flags.auctions) {
        if (family === "auction") auctions = ((h >> 7) % 2800) + 200;   // 200 – 2999
        else auctions = ((h >> 7) % 50);                               //   0 – 49
    }

    // -1 (overdue) through +6 days out, with about 1-in-8 landing on
    // today so the "Closing today" KPI is never empty in the demo.
    let auction_end_date: string | null = null;
    if (flags.auctionEndDate) {
        const offset = ((h >> 11) % 8) - 1;
        auction_end_date = isoDateOffset(offset);
    }

    return { status, totes, auctions, auction_end_date };
};
